/**
 * Card layout: turn the store's hands into a flat list of card placements (world
 * transform + which card face) so the instanced renderer can lay them out. Pure;
 * no rendering. The set of visible cards comes ONLY from what the server told us
 * (own hole cards + others' public up-cards, then all hands at resolution).
 */

import * as THREE from 'three';
import type { Card } from '@blackjack/shared';
import { HOUSE_ID } from '@blackjack/shared';
import { computeSeats, assignSeatIndices, CROUPIER, PLAYER_SEAT_COUNT, type Seat } from './seating';
import type { ServerState } from '../state/store';

/** Card physical dimensions (thin box). Width:height ≈ 0.70 to match the card
 *  ART aspect (measured 42×60 within each atlas cell) so the face isn't stretched
 *  — a normal playing-card proportion. */
export const CARD = {
  width: 0.224,
  height: 0.32,
  thickness: 0.006,
  /** Horizontal fan offset per card in a hand. */
  spread: 0.13,
  /** Slight rotation fan per card (radians). */
  fan: 0.07,
} as const;

export interface CardPlacement {
  /** Stable key for animation continuity. */
  readonly key: string;
  /** World matrix for the instance. */
  readonly matrix: THREE.Matrix4;
  /** The card face (undefined = face-down → render the back). */
  readonly card: Card | undefined;
}

const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3(1, 1, 1);
const _euler = new THREE.Euler();

/** Lay one hand flat on the felt at an anchor, fanned out and facing up. */
function layHand(
  ownerKey: string,
  anchor: THREE.Vector3,
  facing: number,
  cards: readonly (Card | undefined)[],
  out: CardPlacement[],
): void {
  const n = cards.length;
  const startOffset = -((n - 1) * CARD.spread) / 2;
  for (let i = 0; i < n; i++) {
    const along = startOffset + i * CARD.spread;
    // The card box is thin in Y (its big face already points UP), so it lies FLAT
    // on the felt with NO tilt — only a yaw fan about the vertical axis around the
    // owner's facing direction. (A prior -90° X tilt stood the cards upright, which
    // looked wrong and clipped the table.)
    _euler.set(0, facing + (i - (n - 1) / 2) * CARD.fan, 0);
    _quat.setFromEuler(_euler);
    // Offset perpendicular to the facing direction so cards spread sideways.
    const dx = Math.cos(facing) * along;
    const dz = -Math.sin(facing) * along;
    _pos.set(anchor.x + dx, anchor.y + i * (CARD.thickness + 0.001), anchor.z + dz);
    const m = new THREE.Matrix4().compose(_pos, _quat, _scale);
    out.push({ key: `${ownerKey}:${i}`, matrix: m, card: cards[i] });
  }
}

/**
 * Build all visible card placements for the current server state. During
 * play we show our own hand face-up and others' up-cards; at resolution we show
 * every revealed hand.
 */
export function layoutCards(server: ServerState, selfId: string | undefined): CardPlacement[] {
  const out: CardPlacement[] = [];

  // Map each roster player (excluding self + croupier) to a CENTERED subset of the
  // 11 fixed betting spots (same mapping as Blobs/Chips via assignSeatIndices) so
  // cards land on the painted card-slot marks and players spread evenly.
  const players = server.roster.filter((r) => r.role === 'player');
  const seats = computeSeats(PLAYER_SEAT_COUNT);
  const seatIndices = assignSeatIndices(players.length);
  const seatById = new Map<string, Seat>();
  players.forEach((p, i) => {
    const seat = seats[seatIndices[i] ?? i];
    if (seat) seatById.set(p.id, seat);
  });

  if (server.resolution) {
    // Resolution: lay every revealed hand at its seat (croupier at the flat edge).
    for (const h of server.resolution.hands) {
      if (h.id === '__house__') {
        layHand('house', CROUPIER.cardAnchor, CROUPIER.facing, h.hand, out);
        continue;
      }
      const seat = seatById.get(h.id);
      const anchor = seat ? seat.cardAnchor : CROUPIER.cardAnchor;
      const facing = seat ? seat.facing : CROUPIER.facing;
      layHand(h.id, anchor, facing, h.hand, out);
    }
    return out;
  }

  // In-progress: our own hand (face-up to us) + others' public up-cards.
  if (server.ownHand && selfId) {
    const seat = seatById.get(selfId);
    const anchor = seat ? seat.cardAnchor : CROUPIER.cardAnchor;
    const facing = seat ? seat.facing : CROUPIER.facing;
    layHand(selfId, anchor, facing, server.ownHand.hand, out);
  }
  for (const view of server.publicUpcards) {
    if (view.id === selfId) continue; // already laid our own hand
    const seat = seatById.get(view.id);
    const anchor = seat ? seat.cardAnchor : CROUPIER.cardAnchor;
    const facing = seat ? seat.facing : CROUPIER.facing;
    // The house keeps one hole card face-DOWN during play (classic dealer
    // presentation): show the public up-card + a face-down placeholder. The
    // server never sends the hole card — `undefined` renders the card back.
    const cards: readonly (Card | undefined)[] =
      view.id === HOUSE_ID ? [...view.upcards, undefined] : view.upcards;
    layHand(view.id, anchor, facing, cards, out);
  }

  return out;
}
